"use client"
/**
 * header地址信息展示组件
 * 主要功能:
 * 显示当前配送地址邮编
 * 提供地址选择抽屉入口
 * 根据地理位置检测显示国家切换提示
 */

// React
import { useEffect, useState, useRef, useMemo, lazy, Suspense } from "react";
// 三方库/框架
import to from "await-to-js";
import { useTranslation } from "@/i18n/client";
import { usePathname } from "next/navigation";
import { observer } from "mobx-react-lite";
// 公共方法
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/25061";
import { saveUrlExtraParams } from "@/utils/extUrlParamsUtils";
import { getGeolocationCountryCode } from "@/utils/addressUtils";
// Store
import { useStores } from "@/models";
// 组件
import { Popper, Paper, Button, ClickAwayListener } from "@mui/material";
import UKLoadingDots from "@/components/UKLoadingDots";
// 资源
import DeliverSVG from "@/assets/icons/deliver.svg";
import ArrowDown from "@/assets/icons/arrow-down.svg";
import CoordinateIcon from "@/assets/icons/address/CoordinateIcon.svg";
// 样式
import "@/components/header/header.css"
import styles from "./index.module.scss";

interface Props {
  serverPostCode: string
}

const AddressSelectDrawer = lazy(() => import("@/components/address/AddressSelect/AddressSelectDrawer"));

const AddressButton = observer(({ serverPostCode }: Props) => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { countryStore } = useStores();

  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false); // 控制地址选择抽屉的显示状态
  const [showAddressPopper, setShowAddressPopper] = useState<boolean>(false); // 控制地址提示弹层的显示状态

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [currentCountryName, setCurrentCountryName] = useState<string>("");

  useEffect(() => {
    saveUrlExtraParams(); // 保存URL中的额外参数

    // 设置Popper锚点
    if (containerRef.current) {
      setAnchorEl(containerRef.current);
    }
  }, []);

  /**
   * 地址信息更新时，检查是否需要显示国家切换提示
   */
  useEffect(() => {
    if (countryStore.currentAddress) {
      handleCountryAlert();
    }
  }, [countryStore.currentAddress]);

  /**
   * 当前邮编显示文本
   */
  const postCode = useMemo(() => {
    return countryStore?.currentAddress?.postCode ? `${t("common.deliverTo")} ${countryStore.currentAddress.postCode}` : "";
  }, [t, countryStore.currentAddress]);

  /**
   * 根据用户地理位置和当前选择的国家判断是否需要显示切换国家的提示
   */
  const handleCountryAlert = async () => {
    const isHomePage = pathname === "/";
    if (!isHomePage) return; // 非首页不显示提示

    // 判断是否可以切换国家（国家列表中有多个国家选项）
    let canSwitchCountry = false;
    if (countryStore.countryList?.length > 1) {
      canSwitchCountry = true;
    }

    // 检查用户是否点击过国家提示dismiss按钮（每次更新地址选择数据时 dismiss按钮点击状态会重置 因为要触发重新提示）
    const isCountryAlertDismissed = localStorage.getItem("countryAlertDismissed") === "true";
    // 获取设备地理位置对应的国家代码
    const deviceCountryCode = await getGeolocationCountryCode();
    
    // 条件判断：可切换国家 && 未关闭提示 && 当前国家与设备地理位置国家不一致
    if (canSwitchCountry && !isCountryAlertDismissed && countryStore.currentAddress?.countryCode && deviceCountryCode && countryStore.currentAddress.countryCode !== deviceCountryCode) {
      const countryCode = countryStore.currentAddress.countryCode;
      const translationKey = `common.countryName.${countryCode}`;
      const countryName = t(translationKey);

      // 检查国家名称对应的多语言文案是否存在（避免出现翻译错误）
      if (countryName && countryName !== translationKey) {
        setCurrentCountryName(countryName);
        setShowAddressPopper(true); // 显示国家切换提示弹层
      }
    }
  };

  /**
   * 打开地址选择抽屉并关闭提示弹层
   */
  const handlerDeliver = () => {
    setIsOpenDialog(true);
    setShowAddressPopper(false);
  };

  /**
   * dismiss按钮事件
   */
  const handlePopperDismiss = () => {
    setShowAddressPopper(false); // 关闭提示弹层
    localStorage.setItem("countryAlertDismissed", "true"); // 记录用户已关闭提示
  }

  return (
    <div
      className="deliver display-flex"
      data-exptag={"exp|" + EXPOSURE_RECORD.Header_Location_Expo}
      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
      onClick={() => {
        sendClickTrack.Header_Location();
      }}
      ref={containerRef}
    >
      <button
        onClick={handlerDeliver}
        className={`deliver-box display-flex ${styles.deliverWrap}`}
      >
        <DeliverSVG className="deliver-icon" />
        {(postCode || serverPostCode) ? (
          <>{postCode || serverPostCode}</>
        ) : (
          <UKLoadingDots className={styles.loadingWrap} />
        )}
        <ArrowDown className="deliver-arrow" />
      </button>

      {showAddressPopper && (
        <ClickAwayListener onClickAway={() => setShowAddressPopper(false)}>
          <Popper
            open={showAddressPopper}
            anchorEl={anchorEl}
            placement="bottom-end"
            disablePortal={true}
            sx={{
              '&[data-popper-placement*="bottom"] .MuiPaper-root::before': {
                content: '""',
                position: 'absolute',
                top: "-7px",
                right: "15%",
                width: "14px",
                height: "14px",
                backgroundColor: "#FFFFFF",
                transform: "rotate(45deg)",
              }
            }}
          >
            <Paper
              elevation={3}
              className={styles.popperPaper}
            >
              <div className={styles.popperContent}>
                <CoordinateIcon />
                <div className={styles.alertText}>
                  {t("address.headerAlert", { countryName: currentCountryName })}
                </div>
              </div>

              <div className={styles.popperFooter}>
                <Button variant="outlined" size="small" onClick={handlePopperDismiss}>
                  {t("common.btnText.dismiss")}
                </Button>

                <Button variant="contained" size="small" onClick={handlerDeliver}>
                  {t("common.btnText.changeAddress")}
                </Button>
              </div>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      <Suspense fallback={null}>
        <AddressSelectDrawer
          openDrawer={isOpenDialog}
          setOpenDrawer={setIsOpenDialog}
        />
      </Suspense>
    </div>
  )
});

export default AddressButton;
